/**
 * Which data does THIS user need on THIS device?
 *
 * A cashier needs the catalogue and their till's stock; a vet needs their
 * visits, pet history and the clinical catalogues; a driver needs their van
 * load. Downloading everything for everyone would be slow, fat and a privacy
 * problem on a shared tablet.
 *
 * The app already knows who someone is — the backend's access snapshot lists the
 * page keys they were granted — so a pack is a LOOKUP, not a guess and not a new
 * concept for an admin to maintain. Grant `page.pos.sell` and the till pack
 * follows; revoke it and the next refresh drops it.
 *
 * This module only decides and describes packs. Fetching lives in
 * `snapshot.ts`, storage in `idb.ts`.
 */

import { PAGE_KEYS } from "@/config/moduleRegistry/keys";
import { canPage } from "@/helper/permissionHelper";
import {
  idbDelete,
  idbGet,
  idbGetAllByIndex,
  idbPut,
  STORE_PACKS,
} from "./idb";

export type PackId = "pos" | "clinic" | "boarding" | "driver";

export interface PackDefinition {
  id: PackId;
  /** Any one of these page keys is enough to earn the pack. */
  pageKeys: string[];
  /** Datasets this pack stores, in the order they are downloaded. */
  datasets: string[];
  /**
   * How stale this pack may be before the UI says so. Prices move, a room board
   * moves faster, a catalogue barely moves at all.
   */
  staleAfterMs: number;
}

const HOUR = 60 * 60 * 1000;

export const PACK_DEFINITIONS: Record<PackId, PackDefinition> = {
  pos: {
    id: "pos",
    pageKeys: [PAGE_KEYS.POS_SELL],
    // Stock is NOT its own dataset: `GetPosCatalogItems` already returns each
    // row's `qtyAvailable` for the till's warehouse, so splitting it would mean
    // two sources of truth for one number. Delivery partners arrive with the
    // driver work in a later stage.
    datasets: [
      "profile",
      "itemGroups",
      "items",
      "barcodes",
      "customers",
      "paymentModes",
    ],
    staleAfterMs: 12 * HOUR,
  },
  clinic: {
    id: "clinic",
    pageKeys: [
      PAGE_KEYS.HEALTHCARE_VISITS,
      PAGE_KEYS.HEALTHCARE_CASE_SHEETS,
      PAGE_KEYS.CLINIC_STATION,
    ],
    /**
     * ⚠️ ONLY WHAT IS ACTUALLY DOWNLOADED. `readPackFreshness` calls a pack
     * complete when every listed dataset is stored, and the readiness indicator
     * now depends on that answer — so naming the rest would report a fully
     * downloaded device as "preparing" forever. They join this list in the change
     * that starts downloading them.
     *
     * The visit LIST is read-only offline. Clinical writes wait for the backend
     * `apply_ops`: a visit saves as a whole document, so queueing one would
     * overwrite whatever a colleague changed meanwhile.
     */
    datasets: ["visits"],
    staleAfterMs: 12 * HOUR,
  },
  boarding: {
    id: "boarding",
    pageKeys: [PAGE_KEYS.HEALTHCARE_BOARDING],
    /**
     * ⚠️ ONLY WHAT IS ACTUALLY DOWNLOADED MAY BE LISTED HERE.
     *
     * `readPackFreshness` calls a pack complete when every listed dataset is
     * stored, so naming datasets nobody writes leaves the pack permanently
     * "incomplete" — which now drives the readiness indicator and would report a
     * fully downloaded device as not ready. Rates, settings and due-plan items
     * join this list in the change that starts downloading them.
     */
    datasets: ["board"],
    staleAfterMs: 6 * HOUR,
  },
  driver: {
    id: "driver",
    pageKeys: [PAGE_KEYS.DRIVERS_ME],
    datasets: ["profile", "vanLoad", "orders", "customers"],
    staleAfterMs: 6 * HOUR,
  },
};

export const ALL_PACK_IDS = Object.keys(PACK_DEFINITIONS) as PackId[];

/**
 * A stored dataset. Rows are kept as ONE record per dataset rather than one
 * record per row: at this site's real sizes (~450 items, ~2,200 pets) a whole
 * dataset is a single fast read, there are no indexes to maintain, and a refresh
 * is one atomic put — a half-applied catalogue is impossible.
 */
export interface StoredDataset<T = unknown> {
  /** `<user>:<pack>:<dataset>` */
  key: string;
  user: string;
  pack: PackId;
  dataset: string;
  rows: T[];
  /** Epoch ms of the last successful write. */
  syncedAt: number;
  /** Server time of the last successful sync, sent back as `since` next time. */
  watermark: string | null;
  /** Bumped when the shape changes so an old row set is discarded, not misread. */
  schema: number;
  /**
   * WHICH TILL THIS DATA BELONGS TO — the active POS profile name.
   *
   * ⚠️ WITHOUT THIS, A TILL SWITCH SERVES ANOTHER WAREHOUSE'S STOCK. This site has
   * two branches and four POS profiles, each drawing from a different warehouse
   * (`صندوق بكر` and `صندوق عباس` from the clinic hall, `Alkokh Vet Hotel -
   * Cashier` from التينادور). The pack was keyed by USER alone, so a cashier who
   * switched till on the same device kept the previous warehouse's quantities —
   * shown as available, and reserved against by offline sales.
   *
   * Scoped by profile rather than by warehouse because each branch has its own
   * till, and the profile is what the app already knows it is running as.
   */
  scope: string | null;
}

/** Raise this when a dataset's shape changes incompatibly. */
export const DATASET_SCHEMA = 1;

export function datasetKey(user: string, pack: PackId, dataset: string): string {
  return `${user}:${pack}:${dataset}`;
}

/**
 * The packs this user has earned.
 *
 * Reads the cached access snapshot through `canPage`, which is synchronous and
 * works offline — deliberately, because this runs at boot before any network
 * call and must give the same answer on a device that has been offline all day.
 */
export function resolvePacksForUser(): PackId[] {
  return ALL_PACK_IDS.filter(id =>
    PACK_DEFINITIONS[id].pageKeys.some(pageKey => canPage(pageKey)));
}

export function isPackEarned(pack: PackId): boolean {
  return PACK_DEFINITIONS[pack].pageKeys.some(pageKey => canPage(pageKey));
}

export async function readDataset<T>(
  user: string,
  pack: PackId,
  dataset: string,
  /** The till this data must belong to. A mismatch reads as "nothing stored". */
  expectedScope?: string | null,
): Promise<StoredDataset<T> | null> {
  const stored = await idbGet<StoredDataset<T>>(STORE_PACKS, datasetKey(user, pack, dataset));

  if (!stored)
    return null;

  // Data from another till is worse than no data: it looks authoritative and is
  // about a different warehouse. Treated as a miss, which forces a re-download.
  if (expectedScope !== undefined && (stored.scope ?? null) !== (expectedScope ?? null))
    return null;

  // A row set written by an older build may not match what today's code reads.
  // Discarding is the only safe migration: the data is a cache, and re-downloading
  // costs one request, while misreading it would put wrong prices on a screen.
  if (stored.schema !== DATASET_SCHEMA)
    return null;

  return stored;
}

export async function writeDataset<T>(
  user: string,
  pack: PackId,
  dataset: string,
  rows: T[],
  watermark: string | null,
  scope: string | null = null,
): Promise<StoredDataset<T>> {
  const record: StoredDataset<T> = {
    key: datasetKey(user, pack, dataset),
    user,
    pack,
    dataset,
    rows,
    syncedAt: Date.now(),
    watermark,
    schema: DATASET_SCHEMA,
    scope,
  };

  // Not durable-flushed: this is a copy of server data. Losing it to a power cut
  // costs one download, so paying the flush on every refresh would be waste.
  // The outbox, which holds work that exists nowhere else, is a different story.
  await idbPut(STORE_PACKS, record);

  return record;
}

export interface PackFreshness {
  pack: PackId;
  /** Oldest `syncedAt` across the pack's datasets; 0 when nothing is stored. */
  syncedAt: number;
  datasetsStored: number;
  datasetsExpected: number;
  complete: boolean;
  stale: boolean;
}

/**
 * How fresh is this pack on the device?
 *
 * ⚠️ PASS `expectedScope` FOR TILL-SPECIFIC PACKS. Freshness used to be keyed by
 * user and pack alone, so a POS pack downloaded for one till — or, worse, for no
 * till at all — answered "synced, complete" for whichever till asked. The schedule
 * believed the device was up to date and skipped the download the cashier actually
 * needed. Rows from another scope are ignored here, which reports the pack as
 * incomplete and lets the next refresh fetch it.
 */
export async function readPackFreshness(
  user: string,
  pack: PackId,
  expectedScope?: string | null,
): Promise<PackFreshness> {
  const definition = PACK_DEFINITIONS[pack];
  const stored = (await idbGetAllByIndex<StoredDataset>(STORE_PACKS, "user", user))
    .filter(row => row.pack === pack && row.schema === DATASET_SCHEMA)
    .filter(row => expectedScope === undefined || (row.scope ?? null) === (expectedScope ?? null));

  // The OLDEST dataset decides: a pack is only as fresh as its stalest part, and
  // reporting the newest would hide a stock table that stopped updating hours ago.
  const syncedAt = stored.length ? Math.min(...stored.map(row => row.syncedAt)) : 0;
  const complete = definition.datasets.every(dataset =>
    stored.some(row => row.dataset === dataset));

  return {
    pack,
    syncedAt,
    datasetsStored: stored.length,
    datasetsExpected: definition.datasets.length,
    complete,
    stale: !syncedAt || Date.now() - syncedAt > definition.staleAfterMs,
  };
}

/**
 * Drop a pack's stored data — used when a user loses the permission that earned
 * it, or on an explicit "clear downloaded data".
 *
 * Only ever touches cached SERVER data. The outbox and drafts are untouched:
 * queued work belongs to the person who created it, not to a permission.
 */
export async function dropPack(user: string, pack: PackId): Promise<void> {
  const stored = (await idbGetAllByIndex<StoredDataset>(STORE_PACKS, "user", user))
    .filter(row => row.pack === pack);

  for (const row of stored)
    await idbDelete(STORE_PACKS, row.key);
}

/** Remove packs the user no longer has access to. Returns what was dropped. */
export async function dropUnearnedPacks(user: string): Promise<PackId[]> {
  const earned = new Set(resolvePacksForUser());
  const dropped: PackId[] = [];

  for (const pack of ALL_PACK_IDS) {
    if (earned.has(pack))
      continue;

    const freshness = await readPackFreshness(user, pack);

    if (freshness.datasetsStored > 0) {
      await dropPack(user, pack);
      dropped.push(pack);
    }
  }

  return dropped;
}
